import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { SemanticApi, type SemanticArticleSearch } from '@/services/targeting/targetingLookupApi'
import {
  DEFAULT_SEMANTIC_SCORE,
  SEMANTIC_MATCH_LEVELS,
  semanticMatchLevel,
  type SemanticTarget,
  type Targeting,
} from '@/services/targeting/targeting'
import { FieldLabel } from '../FieldLabel'
import { SwitchField } from '../SwitchField'
import { NegateToggle } from './NegateToggle'
import { SemanticArticleResults } from './SemanticArticleResults'

/** A sentence written but not yet added, and the means to add it. */
export interface PendingSemanticTarget {
  sentence: string
  add: () => void
}

export interface SemanticTargetEditorProps {
  value: SemanticTarget[]
  onChange: (value: SemanticTarget[]) => void
  /** The whole targeting being edited — the article search is narrowed by it. */
  targeting: Targeting
  /**
   * Reports a sentence that is written but not added, so the dialog can ask
   * about it rather than quietly dropping it when the targeting is applied.
   */
  onPendingChange?: (pending: PendingSemanticTarget | null) => void
}

/** What the composer holds while a target is being written or changed. */
interface Draft {
  sentence: string
  score: number
  positiveSentimentOnly: boolean
  negated: boolean
}

const emptyDraft = (): Draft => ({
  sentence: '',
  score: DEFAULT_SEMANTIC_SCORE,
  positiveSentimentOnly: false,
  negated: false,
})

/** A sentence shorter than this carries no meaning worth encoding. */
const MIN_SENTENCE_LENGTH = 2

/**
 * Targets articles by meaning rather than by exact words: a sentence is
 * encoded, and an article matches when its own meaning is close enough to it.
 * The article search shows what a sentence and match level would reach before
 * the target is added — or what an existing one reaches now.
 */
export function SemanticTargetEditor({
  value,
  onChange,
  targeting,
  onPendingChange,
}: SemanticTargetEditorProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  /** The target being changed in the composer, or null when adding a new one. */
  const [editing, setEditing] = useState<number | null>(null)
  const [search, setSearch] = useState<SemanticArticleSearch | null>(null)

  const sentence = draft.sentence.trim()
  const canSubmit = sentence.length >= MIN_SENTENCE_LENGTH
  const levelLabel = (score: number) =>
    t(`targeting.semantic.levels.${semanticMatchLevel(score) || 1}`)

  const reset = () => {
    setDraft(emptyDraft())
    setEditing(null)
  }

  // The server only accepts a sentence it has encoded, so the text is sent to
  // the embedding endpoint first and the target built from what comes back.
  // A target whose text is unchanged keeps the sentence it already has.
  const save = useMutation({
    mutationFn: async () => {
      const current = editing === null ? undefined : value[editing]
      return current && current.sentence.sentence === sentence
        ? current.sentence
        : SemanticApi.embed(sentence)
    },
    onSuccess: (embedded) => {
      const target: SemanticTarget = {
        ...(editing === null ? {} : value[editing]),
        sentence: embedded,
        score: draft.score,
        negated: draft.negated,
        positiveSentimentOnly: draft.positiveSentimentOnly,
      }
      onChange(
        editing === null
          ? [...value, target]
          : value.map((row, i) => (i === editing ? target : row))
      )
      reset()
    },
    onError: (error: Error) =>
      toast.error(t('targeting.semantic.embedFailed'), { description: error.message }),
  })

  // `save.mutate` is stable and reads the current draft when called, so the
  // pending handle stays valid as the composer is edited.
  const addPending = save.mutate
  useEffect(() => {
    onPendingChange?.(canSubmit ? { sentence, add: () => addPending() } : null)
    return () => onPendingChange?.(null)
  }, [sentence, canSubmit, onPendingChange, addPending])

  const edit = (index: number) => {
    const target = value[index]
    setEditing(index)
    setDraft({
      sentence: target.sentence.sentence,
      score: target.score,
      positiveSentimentOnly: !!target.positiveSentimentOnly,
      negated: !!target.negated,
    })
  }

  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
    if (editing === index) reset()
    else if (editing !== null && editing > index) setEditing(editing - 1)
  }

  const searchFor = (
    target: Pick<SemanticTarget, 'score' | 'positiveSentimentOnly'> & { sentence: string }
  ) =>
    setSearch({
      sentence: target.sentence,
      score: target.score,
      positiveSentimentOnly: target.positiveSentimentOnly,
    })

  return (
    <div className="flex flex-col gap-5">
      {value.length > 0 && (
        <ItemGroup className="gap-2">
          {value.map((target, index) => (
            <Item
              key={`${target.sentence.id}-${index}`}
              variant={editing === index ? 'muted' : 'outline'}
              size="sm"
            >
              <ItemContent>
                <ItemTitle className="flex-wrap">
                  {target.negated && <Badge variant="red">{t('targeting.keyValue.not')}</Badge>}
                  <span className="whitespace-normal">{target.sentence.sentence}</span>
                </ItemTitle>
                <ItemDescription>
                  {levelLabel(target.score)}
                  {target.positiveSentimentOnly && ` · ${t('targeting.semantic.avoidingNegative')}`}
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    searchFor({
                      sentence: target.sentence.sentence,
                      score: target.score,
                      positiveSentimentOnly: target.positiveSentimentOnly,
                    })
                  }
                >
                  <Search className="size-3.5" />
                  {t('targeting.semantic.findArticles')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('common.edit')}
                  onClick={() => edit(index)}
                >
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('common.remove')}
                  onClick={() => remove(index)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </ItemActions>
            </Item>
          ))}
        </ItemGroup>
      )}

      <div className="flex flex-col gap-4 rounded-lg border p-3">
        <span className="text-sm font-semibold">
          {t(editing === null ? 'targeting.semantic.newTarget' : 'targeting.semantic.editTarget')}
        </span>

        <div className="flex flex-col gap-2">
          <FieldLabel
            htmlFor="semantic-sentence"
            className="text-xs font-medium"
            tooltip={t('targeting.semantic.sentenceHelp')}
          >
            {t('targeting.semantic.sentence')}
          </FieldLabel>
          <Textarea
            id="semantic-sentence"
            rows={3}
            value={draft.sentence}
            placeholder={t('targeting.semantic.sentencePlaceholder')}
            onChange={(e) => setDraft({ ...draft, sentence: e.target.value })}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <FieldLabel className="text-xs font-medium" tooltip={t('targeting.semantic.matchHelp')}>
              {t('targeting.semantic.matchLevel')}
            </FieldLabel>
            <Select
              value={String(draft.score)}
              onValueChange={(score) => setDraft({ ...draft, score: Number(score) })}
            >
              <SelectTrigger className="w-full">
                <SelectValue>{levelLabel(draft.score)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {SEMANTIC_MATCH_LEVELS.map((score) => (
                  <SelectItem key={score} value={String(score)}>
                    {levelLabel(score)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <FieldLabel className="text-xs font-medium">
              {t('targeting.semantic.matchingArticles')}
            </FieldLabel>
            <NegateToggle
              negated={draft.negated}
              onChange={(negated) => setDraft({ ...draft, negated })}
              what={t('targeting.semantic.articles')}
            />
          </div>
        </div>

        <SwitchField
          label={t('targeting.semantic.avoidNegative')}
          tooltip={t('targeting.semantic.avoidNegativeHelp')}
          checked={draft.positiveSentimentOnly}
          onCheckedChange={(positiveSentimentOnly) => setDraft({ ...draft, positiveSentimentOnly })}
        />

        <div className="flex flex-wrap items-center justify-end gap-2">
          {editing !== null && (
            <Button type="button" variant="ghost" onClick={reset}>
              {t('common.cancel')}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            disabled={!canSubmit}
            onClick={() =>
              searchFor({
                sentence,
                score: draft.score,
                positiveSentimentOnly: draft.positiveSentimentOnly,
              })
            }
          >
            <Search className="size-4" />
            {t('targeting.semantic.findArticles')}
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : editing === null ? (
              <Plus className="size-4" />
            ) : (
              <Check className="size-4" />
            )}
            {t(
              editing === null ? 'targeting.semantic.addTarget' : 'targeting.semantic.updateTarget'
            )}
          </Button>
        </div>
      </div>

      {search && (
        <SemanticArticleResults
          search={search}
          targeting={targeting}
          onClose={() => setSearch(null)}
        />
      )}
    </div>
  )
}
